import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

const SITEMAP = {
  Company: [
    { label: "About", href: "#about" },
    { label: "How it works", href: "#how-it-works" },
    { label: "Done-Right Guarantee", to: "/guarantee" },
    { label: "Press", href: "#press" },
    { label: "Careers", href: "#careers" },
  ],
  Services: [
    { label: "Kitchens", href: "#service-kitchens" },
    { label: "Bathrooms", href: "#service-bathrooms" },
    { label: "Basements", href: "#service-basements" },
    { label: "Additions", href: "#service-additions" },
    { label: "All services", href: "#services" },
  ],
  "For pros": [
    { label: "Become a pro", href: "#contractor-signup" },
    { label: "Pro standards", href: "#pro-standards" },
    { label: "Pro dashboard login", href: "#pro-login" },
    { label: "Help centre", href: "#help" },
  ],
  Resources: [
    { label: "Renovation guide", href: "#guide" },
    { label: "Cost calculator", href: "#calculator" },
    { label: "Permit checklist", href: "#permits" },
    { label: "Blog", href: "#blog" },
  ],
};

const SERVICE_AREAS = [
  "Toronto",
  "Mississauga",
  "Oshawa",
  "Brampton",
  "Vaughan",
  "Markham",
  "Richmond Hill",
  "Pickering",
  "Ajax",
  "Whitby",
  "Burlington",
  "Oakville",
];

const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative w-full bg-[#0a0f1c] text-slate-300 overflow-hidden"
    >
      {/* Top hairline */}
      <div className="absolute top-0 left-0 right-0 h-px bg-white/8" />

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-20 lg:pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 mb-16">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <a
              href="/"
              data-testid="footer-logo"
              aria-label="MeinHaus — home"
              className="inline-flex items-center group"
            >
              <img
                src="/logo.svg"
                alt="MeinHaus — Online General Contractor"
                className="h-[42px] w-auto select-none"
                draggable={false}
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </a>
            <p className="mt-5 text-[14px] leading-[1.7] text-slate-400 max-w-[340px]">
              Renovate your home with vetted local pros. Transparent quotes,
              milestone payments held safely, and a Done-Right Guarantee
              backing every build.
            </p>

            {/* Contact */}
            <div className="mt-8 space-y-3 text-[14px]">
              <a
                href="tel:+14165551234"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group"
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(26,160,221,0.15)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2 3 C 2 8, 6 12, 11 12 L 12 9 L 9 8 L 8 9 C 6 8, 5 7, 4 5 L 5 4 L 4 1 Z"
                      stroke="#1AA0DD"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-semibold tracking-tight">
                  +1 (416) 555-1234
                </span>
              </a>
              <a
                href="mailto:hello@meinhaus.ca"
                data-testid="footer-email"
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group"
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(26,160,221,0.15)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M1 3 H13 V11 H1 Z M1 3 L7 8 L13 3"
                      stroke="#1AA0DD"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-semibold tracking-tight">
                  hello@meinhaus.ca
                </span>
              </a>
            </div>

            {/* Socials */}
            <div className="mt-7 flex items-center gap-3">
              {[
                { Icon: Instagram, href: "#instagram", label: "Instagram" },
                { Icon: Facebook, href: "#facebook", label: "Facebook" },
                { Icon: Linkedin, href: "#linkedin", label: "LinkedIn" },
                { Icon: Youtube, href: "#youtube", label: "YouTube" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-white hover:border-white/40 hover:bg-white/5 transition-all"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>

          {/* Sitemap columns */}
          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-8 lg:gap-6">
            {Object.entries(SITEMAP).map(([heading, items]) => (
              <div key={heading}>
                <h4 className="text-[11px] font-bold tracking-[0.22em] uppercase text-white mb-4">
                  {heading}
                </h4>
                <ul className="space-y-2.5">
                  {items.map((item) => (
                    <li key={item.label}>
                      {item.to ? (
                        <Link
                          to={item.to}
                          className="text-[13.5px] text-slate-400 hover:text-[#1AA0DD] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          className="text-[13.5px] text-slate-400 hover:text-[#1AA0DD] transition-colors"
                        >
                          {item.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Service areas */}
          <div className="lg:col-span-3">
            <h4 className="text-[11px] font-bold tracking-[0.22em] uppercase text-white mb-4">
              Service areas
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {SERVICE_AREAS.map((city) => (
                <li key={city}>
                  <a
                    href={`#area-${city.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[13.5px] text-slate-400 hover:text-[#1AA0DD] transition-colors flex items-center gap-1.5"
                  >
                    <span
                      className="block w-1 h-1 rounded-full"
                      style={{ backgroundColor: "#F26B1F" }}
                    />
                    {city}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/8 pt-7 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <p className="text-[12.5px] text-slate-500">
            © 2026 MeinHaus Inc. Renovate without the regret.
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-slate-500">
            <li>
              <Link
                to="/privacy"
                data-testid="footer-privacy"
                className="hover:text-slate-300 transition-colors"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                data-testid="footer-terms"
                className="hover:text-slate-300 transition-colors"
              >
                Terms of Service
              </Link>
            </li>
            <li>
              <Link
                to="/guarantee"
                data-testid="footer-guarantee"
                className="hover:text-slate-300 transition-colors"
              >
                Guarantee Terms
              </Link>
            </li>
            <li>
              <Link
                to="/accessibility"
                data-testid="footer-accessibility"
                className="hover:text-slate-300 transition-colors"
              >
                Accessibility
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Outsized brand watermark at the very bottom */}
      <div
        aria-hidden="true"
        className="relative pointer-events-none select-none overflow-hidden"
      >
        <div
          className="font-black text-center leading-none whitespace-nowrap"
          style={{
            fontSize: "clamp(80px, 18vw, 240px)",
            letterSpacing: "-0.05em",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 90%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            fontFamily: "'Inter', system-ui, sans-serif",
            paddingBottom: "0.05em",
          }}
        >
          meinhaus<span style={{ color: "rgba(242,107,31,0.18)" }}>.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
